import { useEffect, useRef, useState } from 'react';
import { Dropdown, Nav, Tab } from 'react-bootstrap';
import { TableStyled, ThemeTabs } from '../../styles/style';
import { Collapse, Modal, Table } from 'antd';
import momentZ from 'moment-timezone';
import { formatPrice } from '../../utils/functions/formatting_functions';
import { DateTimeFormat, DateTimeFormatDateOnly } from '../../config/constants';
import {
  verticalDotsIcon_path,
  notFoundIllustration_path,
} from '../../assets/index';
import {
  OrderPaymentMethodTag,
  OrderStatusTag,
} from './components/shared_components';
import { ArchivedOrdersWrapper } from './components/archived_orders_components.styled';
import {
  OrderArchiveExpandedComponent,
  FilterBtns,
} from './components/archived_orders_components';
import {
  ORDER_STATUS,
  ORDER_STATUS_GROUPS,
  OrderBrief,
} from '../../data/types/orders';
import { Link, useLocation } from 'react-router-dom';
import { OrdersApiRepository } from '../../data/repositories/orders_repository';
import { formateServerDateToLocaleDate } from '../../utils/functions/time_date_functions';
import { Colors } from '../../config/theme';
import { AddressOwnerType } from '../../data/types/address';
import { requiresSupportAttention } from './functions/order_functions';
import { shiftTypeFormatStartAndEndDateTime } from '../../data/types/shifts';
import { Button, Input, Spacer } from '@nextui-org/react';
import { TbRefreshAlert, TbRefreshDot } from 'react-icons/tb';
import {
  alertMap,
  TIME_TO_REQUIRE_MERCHANT_ATTENTION,
  TIME_TO_REQUIRE_MANAGEMENT_ATTENTION,
} from './functions/active_order_functions';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';

export const OrdersArchive = ({
  ordersRepository,
  orders,
  openOrderDetailsFunction,
  openListingDetailsFunction,
  SideModal,
  isManagement,
  refreshActiveOrders,
  refreshOrders,
}: {
  ordersRepository: OrdersApiRepository;
  orders: OrderBrief[];
  openOrderDetailsFunction: (orderId: number) => void;
  openListingDetailsFunction: (listingId: number) => void;
  isManagement: boolean;
  SideModal?: any;
  refreshActiveOrders: () => void;
  refreshOrders: () => void;
}) => {
  const [shownOrders, setShownOrders] = useState<OrderBrief[]>(orders);
  const [sideOrderID, setSideOrderID] = useState<number | undefined>(undefined);
  const [canvasShow, setCanvasShow] = useState<boolean>(false);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{
    customers: number[];
    date: any;
    sellers: number[];
    shifts: number[];
    transaction_method: number[];
  }>({
    customers: [],
    sellers: [],
    shifts: [],
    date: [],
    transaction_method: [],
  });
  const location = useLocation();
  useEffect(() => {
    setFilters(
      location.state?.filters || {
        customers: [],
        sellers: [],
        shifts: [],
        date: [],
        transaction_method: [],
      },
    );
  }, [location]);

  const [activeTab, setActiveTab] = useState<keyof typeof ORDER_STATUS_GROUPS>(
    location.state?.activeTab || 'active',
  );

  useEffect(() => {
    let results = orders || [];
    const lowerSearch = search.toLowerCase();

    if (
      search !== '' &&
      search !== '#' &&
      lowerSearch !== '#c:' &&
      lowerSearch !== '#o:' &&
      lowerSearch !== '#c' &&
      lowerSearch !== '#o'
    ) {
      const hashtagTrimmedSearch = search.trim().replace('#', '');
      results = results.filter((item) => {
        if (
          search.startsWith('#c:') &&
          String(item.customer_id) === search.trim().replace('#c:', '')
        ) {
          return true;
        }
        if (
          search.startsWith('#o:') &&
          String(item.id) === search.trim().replace('#o:', '')
        ) {
          return true;
        }
        if (
          String(item.id).includes(hashtagTrimmedSearch) ||
          String(item.customer_id).includes(hashtagTrimmedSearch)
        ) {
          return true;
        }
        return false;
      });
    }

    if (filters.customers?.length > 0) {
      results = results.filter((item) =>
        filters.customers?.includes(item.customer_id),
      );
    }

    if (filters.sellers?.length > 0) {
      results = results.filter((item) =>
        filters.sellers?.includes(item.store_id),
      );
    }

    if (filters.shifts?.length > 0) {
      results = results.filter((item) =>
        filters.shifts?.includes(item.last_mile_shift_id),
      );
    }

    if (filters.transaction_method?.length > 0) {
      results = results.filter((item) =>
        filters.transaction_method?.includes(item.payment_type),
      );
    }

    if (filters.date && filters.date?.length > 0) {
      results = results.filter((item) => {
        const given = moment(item.placing_date, DateTimeFormatDateOnly);
        const start = moment(filters.date[0]).startOf('day');
        const end = moment(filters.date[1]).startOf('day');

        return given.diff(start, 'days') >= 0 && given.diff(end, 'days') <= 0;
      });
    }

    if (activeTab && activeTab !== 'all') {
      results = results.filter((item) =>
        ORDER_STATUS_GROUPS[activeTab].has(item.status),
      );
    }

    setShownOrders(results);
  }, [
    filters.date,
    filters.customers,
    filters.sellers,
    filters.shifts,
    filters.transaction_method,
    search,
    activeTab,
    orders?.length,
  ]);

  function getOrderAlertLevel(order: OrderBrief) {
    var level = '';
    const lastMileShift = order?.last_mile_shift;
    const lastShipmentShift =
      order?.shipments?.[order.shipments?.length - 1]?.last_mile_shift;

    const isShiftNow = momentZ().isBetween(
      momentZ(order?.last_mile_shift?.start_datetime),
      momentZ(order?.last_mile_shift?.end_datetime),
    );
    if (isShiftNow) {
      level += alertMap.green.icon;
    }

    const merchantAttentionIsRequired =
      order.status == ORDER_STATUS.accepted ||
      order.status == ORDER_STATUS.requested;
    if (merchantAttentionIsRequired) {
      // if pickupShift start time or last mile shift start time is in less than X minutes and order is still not marked as ready, color it orange
      const timeToShiftStart = momentZ(lastMileShift.end_datetime).diff(
        momentZ(),
        'minutes',
      );
      if (timeToShiftStart > TIME_TO_REQUIRE_MERCHANT_ATTENTION) {
      } else if (
        TIME_TO_REQUIRE_MERCHANT_ATTENTION > timeToShiftStart &&
        timeToShiftStart > 0
      ) {
        level += alertMap.orange.icon;
      } else {
        level += alertMap.black.icon;
      }

      if (order.status == ORDER_STATUS.accepted) {
        level += alertMap.yellow.icon;
      } else {
        level += alertMap.blue.icon;
      }
    }

    const rescheduleAttentionIsRequired =
      order.status == ORDER_STATUS.ship__in_store__waiting_pickup_reschedule ||
      order.status == ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule ||
      order.status == ORDER_STATUS.return__at_silal__waiting_schedule;
    if (rescheduleAttentionIsRequired) {
      level += alertMap.purple.icon;
    }

    const managementAttentionIsRequired =
      order.status == ORDER_STATUS.ship__in_transit__to_customer ||
      order.status == ORDER_STATUS.ship__in_transit__to_silal ||
      order.status ==
        ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer ||
      order.status ==
        ORDER_STATUS.return__in_transit__failed_dropoff_to_store ||
      order.status == ORDER_STATUS.return__in_transit__to_silal ||
      order.status == ORDER_STATUS.return__in_transit__to_store ||
      order.status == ORDER_STATUS.ship__at_silal__ready ||
      order.status == ORDER_STATUS.ship__in_store__ready;

    if (managementAttentionIsRequired) {
      // if end time is in less than 30 minutes, color it red
      // if it has passed, color it brown
      const timeToShiftEnd = momentZ(
        lastShipmentShift?.end_datetime ?? lastMileShift?.end_datetime,
      ).diff(momentZ(), 'minutes');
      if (timeToShiftEnd > TIME_TO_REQUIRE_MANAGEMENT_ATTENTION) {
      } else if (
        TIME_TO_REQUIRE_MANAGEMENT_ATTENTION > timeToShiftEnd &&
        timeToShiftEnd > 0
      ) {
        level += alertMap.brown.icon;
      } else {
        level += alertMap.red.icon;
      }
    }

    return level == '' ? '⚪' : level;
  }
  const columns = isManagement
    ? [
        {
          title: 'ID',
          dataIndex: 'id',
          width: 100,
          sorter: (a: OrderBrief, b: OrderBrief) => a.id - b.id,
          render: (id: number, row: OrderBrief) => (
            <Link
              to={`/databases/orders-archive/${id}/overview`}
              target="_blank"
              style={{ color: Colors.greenMain }}
            >
              {activeTab === 'active' && getOrderAlertLevel(row)} #{id}
            </Link>
          ),
        },
        {
          title: 'C-ID',
          dataIndex: 'customer_id',
          width: 80,
          render: (customer_id: number, row: OrderBrief) => (
            <Link
              title="Store ID or Name if store is the source"
              to={`/databases/customers/${customer_id}/profile`}
              style={{ color: Colors.greenMain }}
              target="_blank"
            >
              #{customer_id}{' '}
              <span style={{ color: Colors.primaryText }}>
                {' '}
                {row.destination_address.display_name}
              </span>
            </Link>
          ),
          sorter: (a: OrderBrief, b: OrderBrief) =>
            a.customer_id - b.customer_id,
        },
        {
          title: 'S-ID/Name',
          dataIndex: 'store_id',
          width: 160,
          render: (store_id: number, row: OrderBrief) => (
            <Link
              title="Store ID or Name if store is the source"
              to={`/databases/stores/${store_id}/current-orders`}
              style={{ color: Colors.greenMain }}
              target="_blank"
            >
              #{store_id}{' '}
              {row.source_address.owner_type === AddressOwnerType.Store ? (
                <span style={{ color: Colors.primaryText }}>
                  {' '}
                  {row.source_address.display_name}
                </span>
              ) : (
                row.source_address.owner_type
              )}
            </Link>
          ),
        },
        {
          title: 'Last Shipment Shift',
          dataIndex: 'shipments',
          width: 150,
          sorter: (a: OrderBrief, b: OrderBrief) =>
            momentZ(
              a.shipments?.[a.shipments?.length - 1]?.last_mile_shift
                ?.start_datetime,
            ).diff(
              momentZ(
                b.shipments?.[b.shipments?.length - 1]?.last_mile_shift
                  ?.start_datetime,
              ),
            ),
          render: (shipments: any[], row: OrderBrief) => {
            const lastShift =
              shipments?.[shipments?.length - 1]?.last_mile_shift;
            const color = requiresSupportAttention(row)
              ? Colors.tomatoRed
              : Colors.greenMain;
            return lastShift ? (
              <Link
                to={`/logistics/shifts/${lastShift.id}`}
                style={{ color }}
                target="_blank"
              >
                #{lastShift.id}
                <br />
                <span style={{ color: Colors.primaryText }}>
                  {shiftTypeFormatStartAndEndDateTime(lastShift).split(' ')[0]}
                  <br />
                  {shiftTypeFormatStartAndEndDateTime(lastShift).split(' ')[1]}
                </span>
              </Link>
            ) : (
              'N/A'
            );
          },
        },
        {
          title: 'Last Mile Shift',
          dataIndex: 'last_mile_shift',
          width: 150,
          sorter: (a: OrderBrief, b: OrderBrief) =>
            momentZ(a.last_mile_shift?.start_datetime).diff(
              momentZ(b.last_mile_shift?.start_datetime),
            ),
          render: (last_mile_shift: any, row: OrderBrief) => {
            const isShiftNow = momentZ().isBetween(
              momentZ(last_mile_shift?.start_datetime),
              momentZ(last_mile_shift?.end_datetime),
            );
            return last_mile_shift ? (
              <Link
                target="_blank"
                to={`/logistics/shifts/${row.last_mile_shift_id}`}
                style={{ color: Colors.greenMain }}
              >
                #{row.last_mile_shift_id}
                <br />
                <span style={{ color: Colors.primaryText }}>
                  {
                    shiftTypeFormatStartAndEndDateTime(last_mile_shift).split(
                      ' ',
                    )[0]
                  }
                  <br />
                  {
                    shiftTypeFormatStartAndEndDateTime(last_mile_shift).split(
                      ' ',
                    )[1]
                  }
                </span>
              </Link>
            ) : (
              'N/A'
            );
          },
        },
        {
          title: 'Payment',
          dataIndex: 'payment_type',
          render: (payment_type: number) => OrderPaymentMethodTag(payment_type),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          width: 120,
          sorter: (a: OrderBrief, b: OrderBrief) => a.status - b.status,
          render: (status: number) => OrderStatusTag(status),
        },
        {
          title: 'Created',
          dataIndex: 'placing_date',
          width: 120,
          sorter: (a: OrderBrief, b: OrderBrief) =>
            momentZ(a.placing_date, DateTimeFormat).diff(
              momentZ(b.placing_date, DateTimeFormat),
            ),
          render: (placing_date: string) => {
            const created = formateServerDateToLocaleDate(placing_date);
            return (
              <>
                {created.split(' ')[0]}
                <br />
                {created.split(' ')[1]}
              </>
            );
          },
        },
        {
          title: 'Last Action',
          dataIndex: 'order_actions',
          width: 120,
          sorter: (a: OrderBrief, b: OrderBrief) =>
            momentZ(
              a.order_actions[a.order_actions?.length - 1]?.action_date,
            ).diff(
              momentZ(
                b.order_actions[b.order_actions?.length - 1]?.action_date,
              ),
            ),
          render: (order_actions: any[]) => {
            const lastAction =
              order_actions[order_actions?.length - 1]?.action_date;
            const formatted = formateServerDateToLocaleDate(lastAction);
            return (
              <>
                {formatted.split(' ')[0]}
                <br />
                {formatted.split(' ')[1]}
              </>
            );
          },
        },
        {
          title: 'Total price',
          dataIndex: 'customer_pays',
          render: (customer_pays: number) => (
            <span>{formatPrice(customer_pays)}</span>
          ),
        },
      ]
    : [
        {
          title: 'ID',
          dataIndex: 'id',
          width: 100,
          sorter: (a: OrderBrief, b: OrderBrief) => a.id - b.id,
        },
        {
          title: 'Customer ID',
          dataIndex: 'customer_id',
          sorter: (a: OrderBrief, b: OrderBrief) =>
            a.customer_id - b.customer_id,
        },
        {
          title: 'Payment',
          dataIndex: 'payment_type',
          width: 150,
          render: (payment_type: number) => OrderPaymentMethodTag(payment_type),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          width: 120,
          sorter: (a: OrderBrief, b: OrderBrief) => a.status - b.status,
          render: (status: number) => OrderStatusTag(status),
        },
        {
          title: 'Created',
          dataIndex: 'placing_date',
          width: 150,
          sorter: (a: OrderBrief, b: OrderBrief) =>
            momentZ(a.placing_date, DateTimeFormat).diff(
              momentZ(b.placing_date, DateTimeFormat),
            ),
          render: (placing_date: string) =>
            formateServerDateToLocaleDate(placing_date),
        },
        {
          title: 'Total price',
          dataIndex: 'customer_pays',
          render: (customer_pays: number) => (
            <span>{formatPrice(customer_pays)}</span>
          ),
        },
        {
          title: 'Action',
          dataIndex: 'action',
          render: (_: any, row: OrderBrief) => (
            <ActionButtons showDetail={() => showOrderDetails(row)} />
          ),
        },
      ];

  const ActionButtons = ({ showDetail }: { showDetail: () => void }) => (
    <Dropdown>
      <Dropdown.Toggle id="dropdown-basic">
        <img src={verticalDotsIcon_path} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={showDetail}>Order details</Dropdown.Item>
        {/* <Dropdown.Item href="#">Need help with this order</Dropdown.Item> // TODO enable when CS connected */}
      </Dropdown.Menu>
    </Dropdown>
  );

  const showOrderDetails = async (row: OrderBrief) => {
    setSideOrderID(row.id);
    setCanvasShow(true);
  };

  useEffect(() => {
    // Attach a click event handler to each table row
    const tableRows = document.querySelectorAll('.rdt_TableRow');
    tableRows.forEach((row) => {
      row.addEventListener('click', handleRowClick);
    });

    // Remove the click event handler when the component unmounts
    return () => {
      tableRows.forEach((row) => {
        row.removeEventListener('click', handleRowClick);
      });
    };
  }, []);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to update shownOrders when orders prop changes
  useEffect(() => {
    // Only update if orders actually changed and is not the initial empty array
    if (orders && orders.length > 0) {
      setShownOrders(orders);
    }
  }, [orders]);

  // Polling effect
  useEffect(() => {
    if (activeTab === 'active') {
      const fetchActiveOrders = () => {
        refreshActiveOrders();
      };

      // Start polling
      pollingIntervalRef.current = setInterval(fetchActiveOrders, 20000);

      // Fetch immediately on mount
      fetchActiveOrders();

      // Cleanup on unmount
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    } else if (pollingIntervalRef.current) {
      // Make sure to clear interval if tab changes
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Remove refreshActiveOrders from dependencies and use a ref instead
  }, [activeTab]);

  const handleRowClick = (event: any) => {
    // TODO: fix any
    // Remove the 'selected' class from all rows
    document.querySelectorAll('.rdt_TableRow').forEach((row) => {
      row.classList.remove('selected');
    });

    // Add the 'selected' class to the clicked row
    event.currentTarget.classList.add('selected');
  };

  const renderColumn = (type: string) => (
    <div className="flex flex-col flex-1">
      <h3 className="text-lg font-semibold text-gray-800">{type}</h3>
      {Object.values(alertMap)
        .filter((alert) => alert.type === type)
        .map((alert, index) => (
          <div
            key={index}
            className="flex flex-col items-start p-2 hover:bg-gray-200 rounded-md"
          >
            <span className="text-m">
              {alert.icon}
              <Spacer x={1} />
              <p className="font-semibold text-gray-800">{alert.title}</p>
            </span>
            <p className="text-sm text-gray-500">{alert.subtitle}</p>
          </div>
        ))}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>
          Orders Archive | Silal {isManagement ? 'Management' : 'Merchant'}
        </title>
      </Helmet>

      <ArchivedOrdersWrapper>
        <div className="archive-content">
          <Tab.Container id="orders-tab-container" defaultActiveKey={activeTab}>
            <ThemeTabs
              className="tabs"
              style={{
                padding: '0px',
                paddingLeft: '15px',
                marginBottom: '0px',
              }}
            >
              <Nav variant="pills">
                <Nav.Item onClick={() => setActiveTab('all')}>
                  <Nav.Link eventKey="all">All orders</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('active')}>
                  <Nav.Link eventKey="active">Active Orders</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('requested')}>
                  <Nav.Link eventKey={'requested'}>
                    Requested & Pending
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('accepted')}>
                  <Nav.Link eventKey={'accepted'}>Preparing</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('ready_to_ship')}>
                  <Nav.Link eventKey={'ready_to_ship'}>Ready to ship</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('shipped')}>
                  <Nav.Link eventKey={'shipped'}>Shipped</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('delivered')}>
                  <Nav.Link eventKey={'delivered'}>Delivered</Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('cancelled_rejected')}>
                  <Nav.Link eventKey={'cancelled_rejected'}>
                    Cancelled & Rejected & Failed
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('in_return')}>
                  <Nav.Link eventKey={'in_return'}>In Return</Nav.Link>
                </Nav.Item>{' '}
                <Nav.Item onClick={() => setActiveTab('returned')}>
                  <Nav.Link eventKey={'returned'}>Returned</Nav.Link>
                </Nav.Item>{' '}
                <Nav.Item onClick={() => setActiveTab('waiting_reschedule')}>
                  <Nav.Link eventKey={'waiting_reschedule'}>
                    Waiting Reschedule
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item onClick={() => setActiveTab('with_captain')}>
                  <Nav.Link eventKey={'with_captain'}>With Captain</Nav.Link>
                </Nav.Item>
              </Nav>
              <div className="flex flex-row gap-2 justify-center align-middle">
                <Input
                  variant="faded"
                  type={'text'}
                  placeholder="Search by #CustomerID or #OrderID, for faster search use #c:ID for customers, and #o:ID for orders"
                  onChange={(e: any) => setSearch(e.target.value)} // TODO fix any
                />
                <Button
                  title="Get All Orders"
                  color="warning"
                  variant="flat"
                  onPress={() => {
                    refreshOrders();
                  }}
                  style={{
                    width: '300px',
                  }}
                  endContent={<TbRefreshDot size={30} />}
                >
                  Get All Orders
                </Button>
                <Button
                  title="Get Active Orders"
                  color="success"
                  style={{
                    width: '300px',
                  }}
                  onPress={() => {
                    refreshActiveOrders();
                  }}
                  endContent={<TbRefreshAlert size={30} />}
                >
                  Get Active Orders
                </Button>
              </div>
            </ThemeTabs>
            <div className="filters">
              <FilterBtns
                orders={orders}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
            {activeTab === 'active' && (
              <Collapse
                className="mb-2 p-2 shadow-sm rounded-md"
                items={[
                  {
                    key: 'active',
                    label: `What does the dots colors mean? ${alertMap.green.icon} ${alertMap.orange.icon} ${alertMap.red.icon}`,
                    children: (
                      <div className="flex flex-row">
                        {renderColumn('General')}
                        {renderColumn('Management')}
                        {renderColumn('Merchant')}{' '}
                      </div>
                    ),
                  },
                ]}
              ></Collapse>
            )}
            <Tab.Content>
              <Tab.Pane eventKey={activeTab}>
                <TableStyled>
                  {shownOrders && shownOrders?.length !== 0 ? (
                    <Table
                      columns={columns as any}
                      dataSource={shownOrders}
                      pagination={{
                        pageSizeOptions: ['30', '50', '100'],
                        position: ['bottomRight'],
                        defaultPageSize: 30,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} orders`,
                      }}
                      expandable={{
                        expandRowByClick: true,
                        expandedRowRender: (row) => (
                          <OrderArchiveExpandedComponent
                            orderId={row.id}
                            storeId={isManagement ? undefined : row.store_id}
                            ordersRepository={ordersRepository}
                            openOrderDetailsFunction={openOrderDetailsFunction}
                            openListingDetailsFunction={
                              openListingDetailsFunction
                            }
                          />
                        ),
                      }}
                      rowKey={(record) => record.id}
                    />
                  ) : (
                    <div className="no-orders">
                      <img src={notFoundIllustration_path} alt="no-orders" />
                      <h3>No orders found</h3>
                    </div>
                  )}
                </TableStyled>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
          {SideModal !== undefined && (
            <Modal
              open={canvasShow}
              className="right-slide-modal w-35"
              footer={false}
              onCancel={() => {
                setCanvasShow(false);
                setSideOrderID(undefined);
              }}
              title={`Order ID #${sideOrderID}`}
            >
              <SideModal
                orderId={sideOrderID}
                openOrderDetailsFunction={openOrderDetailsFunction}
              />
            </Modal>
          )}
        </div>
      </ArchivedOrdersWrapper>
    </>
  );
};
